<template>
  <!-- Edit mode: save/cancel/delete only -->
  <nav v-if="isEditing" class="bottom-nav">
    <button
      v-if="pageEditActions.hasChanges"
      class="bottom-nav-item bottom-nav-item--save"
      @click="pageEditActions.save?.()"
    >
      <svg class="bottom-nav-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
        <polyline points="17 21 17 13 7 13 7 21"></polyline>
        <polyline points="7 3 7 8 15 8"></polyline>
      </svg>
      <span class="bottom-nav-label">Save</span>
    </button>
    <button class="bottom-nav-item" @click="pageEditActions.cancel?.()">
      <svg class="bottom-nav-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
      <span class="bottom-nav-label">Cancel</span>
    </button>
    <button class="bottom-nav-item bottom-nav-item--danger" @click="pageEditActions.delete?.()">
      <svg class="bottom-nav-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="3 6 5 6 21 6"></polyline>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
      </svg>
      <span class="bottom-nav-label">Delete</span>
    </button>
  </nav>

  <!-- Normal mode -->
  <nav v-else class="bottom-nav">
    <router-link to="/" class="bottom-nav-item" exact-active-class="bottom-nav-item--active">
      <span class="bottom-nav-icon">📄</span>
      <span class="bottom-nav-label">Home</span>
    </router-link>
    <router-link to="/search" class="bottom-nav-item" active-class="bottom-nav-item--active">
      <span class="bottom-nav-icon">🔍</span>
      <span class="bottom-nav-label">Search</span>
    </router-link>
    <router-link to="/workbench" class="bottom-nav-item" active-class="bottom-nav-item--active">
      <span class="bottom-nav-icon">🤖</span>
      <span class="bottom-nav-label">AI</span>
    </router-link>
    <template v-if="isWikiPage">
      <button v-if="pageEditActions.canEdit" class="bottom-nav-item" @click="editPage">
        <svg class="bottom-nav-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
        </svg>
        <span class="bottom-nav-label">Edit</span>
      </button>
      <button class="bottom-nav-item" @click="viewHistory">
        <svg class="bottom-nav-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        <span class="bottom-nav-label">History</span>
      </button>
      <button
        v-if="pageEditActions.aiImprove && pageEditActions.canEdit"
        class="bottom-nav-item"
        title="Ask AI to improve this page"
        @click="pageEditActions.aiImprove?.()"
      >
        <span class="bottom-nav-icon">✨</span>
        <span class="bottom-nav-label">AI</span>
      </button>
    </template>
    <button v-else class="bottom-nav-item" @click="ui.toggleMobileNav">
      <span class="bottom-nav-icon">📑</span>
      <span class="bottom-nav-label">Pages</span>
    </button>
    <router-link v-if="auth.isAdmin" to="/settings" class="bottom-nav-item" active-class="bottom-nav-item--active">
      <span class="bottom-nav-icon">⚙️</span>
      <span class="bottom-nav-label">Settings</span>
    </router-link>
    <router-link v-if="auth.isAdmin" to="/admin" class="bottom-nav-item" active-class="bottom-nav-item--active">
      <span class="bottom-nav-icon">🛡️</span>
      <span class="bottom-nav-label">Admin</span>
    </router-link>
    <router-link v-if="auth.isAuthenticated" to="/profile" class="bottom-nav-item" active-class="bottom-nav-item--active">
      <span class="bottom-nav-icon">👤</span>
      <span class="bottom-nav-label">Profile</span>
    </router-link>
  </nav>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUiStore } from '../stores/ui'
import { useAuthStore } from '../stores/auth'

const route = useRoute()
const router = useRouter()
const ui = useUiStore()
const auth = useAuthStore()

const isWikiPage = computed(() => route.path.startsWith('/wiki/'))
const isEditing = computed(() => route.query.edit === 'true')

const pageEditActions = inject<{ save?: () => Promise<void>; cancel?: () => void; delete?: () => Promise<void>; aiImprove?: () => void; hasChanges?: boolean; canEdit?: boolean }>('pageEditActions')!

const editPage = () => {
  router.push({ path: route.path, query: { edit: 'true' } })
}

const viewHistory = () => {
  router.push(route.path.replace('/wiki', '/history'))
}
</script>

<style scoped>
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-around;
  height: var(--bottom-nav-height);
  background-color: var(--color-surface);
  border-top: 1px solid var(--color-border);
  z-index: 150;
  padding-bottom: env(safe-area-inset-bottom, 0);
  overflow-x: hidden;
}

.bottom-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  flex: 1;
  height: 100%;
  text-decoration: none;
  color: var(--color-text-muted);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  transition: color var(--transition-fast);
}

.bottom-nav-item:hover,
.bottom-nav-item--active {
  color: var(--color-primary);
}

.bottom-nav-item--save {
  color: var(--color-success);
}

.bottom-nav-item--danger {
  color: var(--color-danger);
}

.bottom-nav-icon {
  font-size: 20px;
  line-height: 1;
}

.bottom-nav-label {
  font-size: 11px;
  font-weight: 500;
}
</style>
